import type { DataGridColumn, DataGridFilterConfig, DataGridGroupColumn, DataGridLeafColumn } from '@ibsys/kit';
import type { TFunction } from 'i18next';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Conversation2ExpertReportInListItem, ConversationListItem } from '@/entities/conversations';
import {
  EXPERT_REPORT_STATUS,
  getExpertReportStatusLabel,
  getExpertReportResultText,
  getVerificationStatusLabel,
  UNKNOWN_RESULT_SHAPE,
  VERIFICATION_STATUSES,
  type ExpertReportBlockTreeNode,
  type ExpertReportBlocksTreeMap,
  type ExpertReportResult,
  type ExpertReportResultShape,
  type ExpertReportResultShapes,
  type ExpertReportResultShapesMap,
} from '@/entities/expert-report';
import { normalizeRichTextSpacing, richTextToReadableText } from '@/shared/lib';
import { formatHumanDate } from '@/shared/lib/utils/date';
import { RichTextContent, sanitizeRichTextHtml, withCopyableCellHover } from '@/shared/ui';
import { hasOwnBlockResult } from './expertReportBlockResult';
import { EMPTY_CELL, EXPERT_REPORT_STANDARD_COLUMNS, reportBlockColumnId, reportColumnId } from './constants';
import { getQualityZoneCellClassName, getRatingCellClassName } from './ratingCellClassName';
import { renderCopyableCell } from './renderCopyableCell';
import { withWrappedText } from './withWrappedText';

/** Параметры построения колонок экспертных заключений */
type UseDialogReportColumnsParams = {
  /** Все доступные экспертные заключения — каждое отображается как отдельная группа колонок */
  allReports: { id: number; name: string }[];
  /** Деревья блоков экспертных заключений: reportId → дерево блоков (для вложенных групп колонок) */
  blocksByReportId: ExpertReportBlocksTreeMap;
  /** Чем оценивается итог и каждый блок видимых заключений — задаёт вид ячейки и вид отбора */
  resultShapesByReportId: ExpertReportResultShapesMap;
};

/**
 * Отбор по колонке результата: у результата-значения и результата-набора — выбор из справочника
 * блока, у балла — диапазон, как прежде. Отобранной по набору сервер считает запись, в наборе
 * которой выбранное значение есть. Блок с совмещённой шкалой отбирается по своему справочнику —
 * как всякий блок, которому справочник определён.
 */
const getResultFilter = (shape: ExpertReportResultShape): DataGridFilterConfig =>
  shape.possibleValues?.length
    ? { type: 'multiselect', options: shape.possibleValues.map((value) => ({ value, label: value })) }
    : { type: 'integer' };

/**
 * Ячейка результата: значение справочника, набор значений перечислением, балл с размерностью
 * либо прочерк. Максимум в ячейку не пишется — он остаётся только основанием цветовой зоны.
 */
const renderResultCell = (result: ExpertReportResult) => {
  const text = getExpertReportResultText(result);
  if (text === null) return <span className="text-muted-foreground">{EMPTY_CELL}</span>;
  return renderCopyableCell(text, text);
};

/**
 * Ячейка резюме: оформление резюме отрисовывается, а не показывается тегами, в буфер обмена
 * уходит тот же текст без разметки. Показано резюме так же, как читается этот текст
 * ({@link normalizeRichTextSpacing}): иначе, например, серия пустых строк стояла бы на экране
 * целиком, а в буфер и в файл попадала одной.
 */
const renderSummaryCell = (summary: string) => {
  const html = sanitizeRichTextHtml(summary);
  return renderCopyableCell(richTextToReadableText(html), <RichTextContent html={normalizeRichTextSpacing(html)} />, {
    isContentWrapped: true,
  });
};

/**
 * Строит листовую колонку с результатом блока. ID — `expertReport_{reportId}_block_{blockId}`,
 * тот же, что и у блока без детей: по нему видимость колонки считает {@link collectResultBlockIds}
 * и `useDialogSearchParams`, независимо от того, стал ли блок ещё и группой.
 *
 * @param header - Заголовок колонки; по умолчанию название блока. У самодостаточной колонки
 * блока с детьми ({@link buildBlockColumn}) переопределяется на «Название. Итог» — иначе она
 * называлась бы так же, как объединяющая её группа.
 */
const buildBlockLeafColumn = (
  block: ExpertReportBlockTreeNode,
  reportId: number,
  shapes: ExpertReportResultShapes | undefined,
  getBlockResult: (item: ConversationListItem, reportId: number, blockId: number) => ExpertReportResult,
  header: string = block.name,
): DataGridLeafColumn<ConversationListItem> =>
  withCopyableCellHover({
    id: reportBlockColumnId(reportId, block.id),
    header,
    size: 120,
    enableSorting: true,
    enableFiltering: true,
    filter: getResultFilter(shapes?.blocks.get(block.id) ?? UNKNOWN_RESULT_SHAPE),
    cellClassName: (ctx) => {
      const { score, maxScore } = getBlockResult(ctx.row, reportId, block.id);
      return getRatingCellClassName(score, maxScore);
    },
    cell: ({ row }) => renderResultCell(getBlockResult(row, reportId, block.id)),
  });

/**
 * Строит колонку блока экспертного заключения рекурсивно из дерева. Блок с дочерними блоками
 * становится вложенной группой колонок; собственный результат у него есть или нет — решает
 * {@link hasOwnBlockResult}, и тогда его колонка добавляется последней среди колонок потомков
 * (см. {@link buildBlockLeafColumn}) с заголовком «Название блока. Итог», чтобы не совпадать
 * с заголовком объединяющей её группы. Блок без детей — колонка с результатом по тому же правилу.
 * Состав колонок совпадает с {@link collectResultBlockIds}. Пустая группа без детей-колонок
 * не рендерится совсем. Кит поддерживает вложенность групп на любую глубину, протестировано
 * до 4 уровней (см. `DataGrid.md` в registry кита) — дерево блоков реальных заключений в это
 * ограничение укладывается.
 */
const buildBlockColumn = (
  block: ExpertReportBlockTreeNode,
  reportId: number,
  shapes: ExpertReportResultShapes | undefined,
  getBlockResult: (item: ConversationListItem, reportId: number, blockId: number) => ExpertReportResult,
  t: TFunction<['dialogs', 'common']>,
): DataGridColumn<ConversationListItem> | null => {
  const hasOwnResult = hasOwnBlockResult(block);

  if (block.children?.length) {
    const children = block.children
      .map((child) => buildBlockColumn(child, reportId, shapes, getBlockResult, t))
      .filter((column): column is DataGridColumn<ConversationListItem> => column !== null);

    if (hasOwnResult) {
      const selfHeader = t('dialogs:expertReport.blockTotal', { name: block.name });
      children.push(buildBlockLeafColumn(block, reportId, shapes, getBlockResult, selfHeader));
    }

    if (!children.length) return null;
    return {
      id: `${reportBlockColumnId(reportId, block.id)}_group`,
      header: block.name,
      columns: children,
    } satisfies DataGridGroupColumn<ConversationListItem>;
  }

  if (!hasOwnResult) return null;

  return buildBlockLeafColumn(block, reportId, shapes, getBlockResult);
};

/**
 * Колонки экспертных заключений для таблицы диалогов: стандартные поля заключения, его итог и
 * колонка на каждый блок.
 *
 * Колонка итога и колонка блока показывают результат в том виде, в каком он получен: значение
 * справочника — значением, набор — перечислением, балл — баллом с размерностью и без максимума.
 * Отбор по колонке-значению идёт выбором из возможных значений, упорядочивание — по порядку
 * справочника (его задаёт сервер). Колонка резюме отбирается текстом, как прочие текстовые
 * колонки: остаются диалоги, в резюме которых введённое встречается целиком.
 */
export const useDialogReportColumns = ({
  allReports,
  blocksByReportId,
  resultShapesByReportId,
}: UseDialogReportColumnsParams): DataGridColumn<ConversationListItem>[] => {
  const { t, i18n } = useTranslation(['dialogs', 'common']);

  const getReportItem = useCallback(
    (item: ConversationListItem, reportId: number): Conversation2ExpertReportInListItem | undefined =>
      item.conversation2expertReport?.find((r) => r.expertReport.id === reportId),
    [],
  );

  const getReportTotal = useCallback(
    (item: ConversationListItem, reportId: number): ExpertReportResult => {
      const found = getReportItem(item, reportId);
      return {
        score: found?.rating ?? null,
        maxScore: found?.maxRating ?? null,
        value: found?.value ?? null,
        values: found?.values ?? null,
        unit: found?.unit ?? null,
      };
    },
    [getReportItem],
  );

  const getBlockResult = useCallback(
    (item: ConversationListItem, reportId: number, blockId: number): ExpertReportResult => {
      const block = getReportItem(item, reportId)?.blocks?.find((b) => b.blockId === blockId);
      return {
        score: block?.score ?? null,
        maxScore: block?.maxScore ?? null,
        value: block?.value ?? null,
        values: block?.values ?? null,
        unit: block?.unit ?? null,
      };
    },
    [getReportItem],
  );

  return useMemo((): DataGridColumn<ConversationListItem>[] => {
    return allReports.map((report) => {
      const rId = report.id;
      const shapes = resultShapesByReportId.get(rId);
      const subColumns: DataGridLeafColumn<ConversationListItem>[] = [];

      EXPERT_REPORT_STANDARD_COLUMNS.forEach((column) => {
        const colId = reportColumnId(rId, column);

        if (column === 'id') {
          subColumns.push({
            id: colId,
            header: t('dialogs:expertReport.id'),
            size: 100,
            enableSorting: true,
            enableFiltering: true,
            filter: { type: 'integer' },
            cell: ({ row }) => {
              const item = getReportItem(row, rId);
              if (!item?.id) return <span className="text-muted-foreground">{EMPTY_CELL}</span>;
              return renderCopyableCell(String(item.id), <span className="font-roboto">{item.id}</span>);
            },
          });
          return;
        }

        if (column === 'startedAt' || column === 'finishedAt') {
          const header =
            column === 'startedAt'
              ? t('dialogs:dialogListSection.expertReport.startedAt')
              : t('dialogs:dialogListSection.expertReport.finishedAt');
          subColumns.push({
            id: colId,
            header,
            size: 160,
            enableSorting: true,
            enableFiltering: true,
            filter: { type: 'date-range' },
            cell: ({ row }) => {
              const at = getReportItem(row, rId)?.[column];
              if (!at) return <span className="text-muted-foreground">{EMPTY_CELL}</span>;
              const formatted = formatHumanDate(i18n, at, { withTime: true });
              return renderCopyableCell(formatted, <span className="font-roboto">{formatted}</span>);
            },
          });
          return;
        }

        if (column === 'rating') {
          subColumns.push({
            id: colId,
            header: t('dialogs:expertReport.total'),
            size: 110,
            enableSorting: true,
            enableFiltering: true,
            filter: getResultFilter(shapes?.total ?? UNKNOWN_RESULT_SHAPE),
            cellClassName: (ctx) => {
              const { score, maxScore } = getReportTotal(ctx.row, rId);
              return getQualityZoneCellClassName(score, maxScore);
            },
            cell: ({ row }) => renderResultCell(getReportTotal(row, rId)),
          });
          return;
        }

        if (column === 'summary') {
          subColumns.push(
            withWrappedText({
              id: colId,
              header: t('dialogs:dialogListSection.expertReport.summary'),
              size: 240,
              // Резюме — свободный текст результата: отбор по нему текстом, упорядочивания нет
              enableSorting: false,
              enableFiltering: true,
              filter: { type: 'text' },
              cell: ({ row }) => {
                const summary = getReportItem(row, rId)?.summary;
                if (!summary) return <span className="text-muted-foreground">{EMPTY_CELL}</span>;
                return renderSummaryCell(summary);
              },
            }),
          );
          return;
        }

        if (column === 'verificationStatus') {
          subColumns.push({
            id: colId,
            header: t('dialogs:dialogListSection.expertReport.verificationStatus'),
            size: 150,
            // Стадия проверки идёт своим ходом, упорядочивания у неё нет — как и у статуса расчёта
            enableSorting: false,
            enableFiltering: true,
            filter: {
              // Набор разворачивается в несколько элементов (OR) — см. buildConversation2ExpertReportFilters
              type: 'multiselect',
              options: VERIFICATION_STATUSES.map((status) => ({
                value: status,
                label: getVerificationStatusLabel(t, status),
              })),
            },
            cell: ({ row }) => {
              const verificationStatus = getReportItem(row, rId)?.verificationStatus;
              if (!verificationStatus) return <span className="text-muted-foreground">{EMPTY_CELL}</span>;
              const label = getVerificationStatusLabel(t, verificationStatus);
              return renderCopyableCell(label, label);
            },
          });
          return;
        }

        if (column === 'status') {
          subColumns.push({
            id: colId,
            header: t('dialogs:dialogListSection.expertReport.status'),
            size: 130,
            enableFiltering: true,
            filter: {
              // conversation2expertReport, набор разворачивается в несколько элементов (OR) —
              // см. buildConversation2ExpertReportFilters
              type: 'multiselect',
              options: (Object.values(EXPERT_REPORT_STATUS) as (keyof typeof EXPERT_REPORT_STATUS)[]).map((s) => ({
                value: s,
                label: getExpertReportStatusLabel(t, s),
              })),
            },
            cell: ({ row }) => {
              const item = getReportItem(row, rId);
              if (!item?.status) return <span className="text-muted-foreground">{EMPTY_CELL}</span>;
              const status = getExpertReportStatusLabel(t, item.status);
              return renderCopyableCell(status, status);
            },
          });
        }
      });

      // Блоки отчёта: дерево вложенных групп (блоки с потомками) и колонок с результатом
      // (листовые блоки); появляются по мере загрузки
      const blockColumns = (blocksByReportId.get(rId) ?? [])
        .map((block) => buildBlockColumn(block, rId, shapes, getBlockResult, t))
        .filter((column): column is DataGridColumn<ConversationListItem> => column !== null);

      return {
        id: `expertReport_${rId}`,
        header: report.name,
        columns: [...subColumns.map(withCopyableCellHover), ...blockColumns],
      } as DataGridGroupColumn<ConversationListItem>;
    });
  }, [allReports, blocksByReportId, resultShapesByReportId, getReportItem, getReportTotal, getBlockResult, i18n, t]);
};
